import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '../session/session-context.js';
import type { ApiClientOptions, ApiResult } from './client.js';
import { resolveBaseUrl } from './endpoints.js';

/**
 * Debounce a fast-changing value (e.g. a search box). Returns the latest value
 * after `delayMs` of quiescence, so a useApiQuery keyed on it fires one request
 * per typing pause instead of per keystroke. The AbortController in useApiQuery
 * then drops any stale in-flight response when the next debounced value lands.
 */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export type QueryState<T> =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ok'; readonly data: T }
  | {
      readonly kind: 'error';
      readonly reason: string;
      readonly missingCapability?: string | null;
    }
  | { readonly kind: 'auth_required' };

/**
 * Extra fields layered on top of {@link QueryState} for stale-while-revalidate
 * background polling (2026-06-09 — mailcom summary flicker fix).
 *
 *  - `refreshing`: a background reload is in flight while previous `ok` data is
 *    still being shown. Consumers render a subtle indicator instead of a full
 *    LoadingState — the panel never blanks/remounts on a poll.
 *  - `version`: monotonic counter bumped on every *settled* response (ok OR
 *    error). Lets a consumer keep a memo honest — a changed payload always
 *    re-derives even if the surrounding `kind` is unchanged. This is the
 *    no-silent-stale guarantee: we never key derived state on a value that can
 *    stay equal across a fresh fetch.
 */
export type QueryControls = {
  readonly reload: () => void;
  /** A background revalidation is in flight while stale `ok` data is shown. */
  readonly refreshing: boolean;
  /** Bumps on every settled response so memos can't go silently stale. */
  readonly version: number;
  /** Last failed background refresh. Data stays visible but is not current. */
  readonly refreshError: string | null;
};

/**
 * Compute a tiny order-independent fingerprint of a settled result so we only
 * bump `version` (and thus force consumers to re-derive) when the payload
 * actually changed. Inlined — no new dependency. JSON.stringify is sufficient
 * here: these projection payloads are small, flat-ish count maps.
 */
function fingerprintResult<T>(res: ApiResult<T>): string {
  try {
    return res.kind === 'ok' ? `ok:${JSON.stringify(res.data)}` : `nok:${res.kind}`;
  } catch {
    // Cyclic / non-serializable payload — fall back to a always-changing token
    // so we never *silently* withhold a fresh response.
    return `raw:${Math.random()}`;
  }
}

export function useApiQuery<T>(
  runner: (options: ApiClientOptions) => Promise<ApiResult<T>>,
  deps: readonly unknown[] = [],
): QueryState<T> & QueryControls {
  const session = useSession();
  const [state, setState] = useState<QueryState<T>>({ kind: 'idle' });
  const [refreshing, setRefreshing] = useState(false);
  const [version, setVersion] = useState(0);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  // Last settled fingerprint — used to bump `version` only on real change.
  const fingerprintRef = useRef<string | null>(null);
  // Whether we currently hold displayable `ok` data. Drives stale-while-
  // revalidate: a reload over existing data must NOT blank the panel.
  const hasDataRef = useRef(false);
  const activeRequest = useRef<AbortController | null>(null);

  const reload = useCallback(() => {
    // A poll must not repeatedly abort a slower request. Dependency changes
    // still cancel the previous selection through the effect cleanup.
    if (!activeRequest.current) setTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (session.state.kind !== 'cookie_session') {
      hasDataRef.current = false;
      fingerprintRef.current = null;
      setRefreshing(false);
      setRefreshError(null);
      setState(previous => previous.kind === 'idle' ? previous : { kind: 'idle' });
      return;
    }
    const controller = new AbortController();
    activeRequest.current = controller;
    // Stale-while-revalidate: only flip to the blanking `loading` state on the
    // FIRST load (no data yet). Subsequent polls keep prior `ok`/`error` data
    // visible and merely raise the `refreshing` flag.
    if (hasDataRef.current) {
      setRefreshing(true);
    } else {
      setState({ kind: 'loading' });
    }
    runner({ baseUrl: resolveBaseUrl(), signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;
        const next = mapQueryResult<T>(res);
        // Stale-while-revalidate: client.ts RESOLVES (never throws) on
        // network/server errors, so a transient failed poll lands here, not in
        // .catch(). While we already hold `ok` data, a transient `error` must
        // NOT blank the panel nor flip hasDataRef (else the next success
        // re-enters the loading/blank path and the flicker returns). A genuine
        // `auth_required` still surfaces so the session-expiry UI can show.
        if (next.kind === 'error' && hasDataRef.current) {
          setRefreshError(next.reason);
          setRefreshing(false);
          return;
        }
        setRefreshError(null);
        const hadOkData = hasDataRef.current;
        hasDataRef.current = next.kind === 'ok';
        const fp = fingerprintResult<T>(res);
        // ok→ok settle with a byte-identical payload (2026-06-10 flicker fix,
        // RC3): keep the EXISTING data identity — skip the setState — so
        // consumer subtrees don't re-commit for a no-op poll. `version` stays
        // put by definition (fingerprint unchanged); only the refreshing flag
        // settles. Any other transition (first load, error→ok, ok→auth, a
        // changed payload) still commits below — never silently stale.
        if (next.kind === 'ok' && hadOkData && fp === fingerprintRef.current) {
          setRefreshing(false);
          return;
        }
        if (fp !== fingerprintRef.current) {
          fingerprintRef.current = fp;
          setVersion((v) => v + 1);
        }
        setState(next);
        setRefreshing(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        // Keep prior data on a transient poll failure (stale-while-revalidate);
        // only surface an error state if we have nothing to show.
        setRefreshing(false);
        if (hasDataRef.current) setRefreshError(err instanceof Error ? err.message : String(err));
        if (!hasDataRef.current) {
          setState({
            kind: 'error',
            reason: err instanceof Error ? err.message : String(err),
          });
        }
      }).finally(() => {
        if (activeRequest.current === controller) activeRequest.current = null;
      });
    return () => {
      controller.abort();
      if (activeRequest.current === controller) activeRequest.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.state, tick, ...deps]);

  return useMemo(
    () => ({ ...state, reload, refreshing, version, refreshError }),
    [state, reload, refreshing, version, refreshError],
  );
}

/** Bounded polling only while this view is visible. Focus/visibility restore
 * refresh immediately; callbacks can change without restarting the interval. */
export function useVisiblePolling(callback: () => void | Promise<void>, intervalMs: number, enabled = true): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    let busy = false;
    const poll = async (): Promise<void> => {
      if (!active || busy || document.visibilityState === 'hidden') return;
      busy = true;
      try { await callbackRef.current(); }
      catch { /* Query owners expose errors; always release the polling guard. */ }
      finally { busy = false; }
    };
    const refresh = () => { void poll(); };
    const timer = window.setInterval(refresh, Math.max(1000, intervalMs));
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [intervalMs, enabled]);
}

export type MutationState<T> =
  | { readonly kind: 'idle' }
  | { readonly kind: 'running' }
  | { readonly kind: 'ok'; readonly data: T }
  | {
      readonly kind: 'error';
      readonly reason: string;
      readonly missingCapability?: string | null;
    }
  | { readonly kind: 'auth_required' };

export function useApiMutation<TInput, TOutput>(
  runner: (
    options: ApiClientOptions,
    input: TInput,
  ) => Promise<ApiResult<TOutput>>,
): {
  readonly state: MutationState<TOutput>;
  readonly run: (input: TInput) => Promise<MutationState<TOutput>>;
  readonly reset: () => void;
} {
  const session = useSession();
  const [state, setState] = useState<MutationState<TOutput>>({ kind: 'idle' });

  const reset = useCallback(() => setState({ kind: 'idle' }), []);

  const run = useCallback(
    async (input: TInput): Promise<MutationState<TOutput>> => {
      if (session.state.kind !== 'cookie_session') {
        const next: MutationState<TOutput> = { kind: 'auth_required' };
        setState(next);
        return next;
      }
      setState({ kind: 'running' });
      const res = await runner({ baseUrl: resolveBaseUrl() }, input);
      const next = mapMutationResult<TOutput>(res);
      setState(next);
      return next;
    },
    [session.state, runner],
  );

  return { state, run, reset };
}

function mapQueryResult<T>(res: ApiResult<T>): QueryState<T> {
  switch (res.kind) {
    case 'ok':
      return { kind: 'ok', data: res.data };
    case 'auth_required':
      return { kind: 'auth_required' };
    case 'forbidden':
      return { kind: 'error', reason: 'forbidden', missingCapability: res.missingCapability };
    case 'error':
      return { kind: 'error', reason: res.reason };
    case 'network_error':
      return { kind: 'error', reason: `network:${res.message}` };
  }
}

function mapMutationResult<T>(res: ApiResult<T>): MutationState<T> {
  switch (res.kind) {
    case 'ok':
      return { kind: 'ok', data: res.data };
    case 'auth_required':
      return { kind: 'auth_required' };
    case 'forbidden':
      return { kind: 'error', reason: 'forbidden', missingCapability: res.missingCapability };
    case 'error':
      return { kind: 'error', reason: res.reason };
    case 'network_error':
      return { kind: 'error', reason: `network:${res.message}` };
  }
}
